import { isKarmaTestEnv } from '../env';
import {
  createInsertionPoint,
  INSERTION_POINT_FILLER,
  isInsertionPoint,
  trimInsertionPointFiller,
} from '../insertionPoint';
import {
  findAncestor,
  findNextChildOrSibling,
  findNextNonWhitespaceSibling,
  findNextSiblingOrAuncle,
  findPreviousNonWhitespaceSibling,
  isElement,
  isText,
} from './node';
import {
  createPointAtEndOfNode,
  createPointAtStartOfNode,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
  createRangeWithPoints,
  Point,
  PointAffinity,
  PointHoistOptions,
} from './point';
import { clampRange } from './range';

type PaintOptions = {
  shouldPaintNode?: (node: Node) => boolean;
  shouldSplitElement?: (element: Element) => boolean;
  createPaintedElement?: (document: Document) => Element;
  // TODO
  paintElement?: (element: Element) => Node | null;
  // TODO
  forcePaintElement?: (element: Element) => Node | null;
};

const DEFAULT_PAINT_OPTIONS = {
  shouldPaintNode: () => true,
  shouldSplitElement: () => false,
  paintElement: () => undefined,
  forcePaintElement: () => undefined,
};

const BAIL_MESSAGE = 'Bailed out of potential infinite loop while painting phrasing content';

export function paintNodesInRange(container: Node, uncontainedRange: Range, options: PaintOptions): Range {
  const range = clampRange(uncontainedRange, container);
  if (!range) {
    return uncontainedRange;
  }

  const document = container.ownerDocument!;

  const { shouldPaintNode, shouldSplitElement, createPaintedElement, paintElement } = {
    ...DEFAULT_PAINT_OPTIONS,
    ...options,
  };

  const pointHoistOptions = {
    // Only hoist up to content that needs painting.
    condition: (node: Node) => shouldPaintNode(node),
    // Split all text nodes and whitelisted elements, except within an insertion point split everything.
    splitCondition: (node: Node) =>
      findAncestor(node, ancestor => isInsertionPoint(ancestor), container)
        ? !isInsertionPoint(node)
        : isText(node) || (isElement(node) && shouldSplitElement(node)),
    // Make sure we remain the first non-whitespace child.
    startCondition: (node: Node) =>
      !isInsertionPoint(node.parentNode) && !findPreviousNonWhitespaceSibling(node),
    // Make sure we remain the last non-whitespace child.
    endCondition: (node: Node) => !isInsertionPoint(node.parentNode) && !findNextNonWhitespaceSibling(node),
  };

  const startPointHoistOptions: PointHoistOptions = {
    ...pointHoistOptions,
    splitAffinity: PointAffinity.START,
  };

  const endPointHoistOptions: PointHoistOptions = {
    ...pointHoistOptions,
    splitAffinity: PointAffinity.END,
  };

  // If the range is collapsed in an element with only a <br> filler, check it for need of paint.
  if (
    range.collapsed &&
    range.endContainer.childNodes.length === 1 &&
    range.endContainer.firstChild &&
    range.endContainer.firstChild.nodeName === 'BR' &&
    shouldPaintNode(range.endContainer.firstChild)
  ) {
    const breakNode = range.endContainer.firstChild;

    // Replace the block filler with an empty text node, so that it'll be replaced by an inline filler below.
    const textNode = document.createTextNode('');
    range.endContainer.replaceChild(textNode, breakNode);

    range.setStart(textNode, 0);
    range.setEnd(textNode, 0);
  }

  // If our collapsed range does not need paint at this point, abort.
  if (range.collapsed && (!isText(range.endContainer) || !shouldPaintNode(range.endContainer))) {
    return range;
  }

  // If the range is collapsed, add and select a zero-width space as an inline filler to apply style to.
  const isPaintingFiller = range.collapsed && isText(range.endContainer);
  let existingInsertionPoint: Node | null = null;

  if (isPaintingFiller) {
    const textNode = range.endContainer;
    existingInsertionPoint = findAncestor(textNode, isInsertionPoint, container);

    // Add the filler at the offset, stripping out any existing filler.
    const text = textNode.nodeValue || '';
    const textBeforeOffset = trimInsertionPointFiller(text.substring(0, range.startOffset));
    textNode.nodeValue = `${textBeforeOffset}${INSERTION_POINT_FILLER}${text.substring(range.startOffset)}`;

    range.setStart(textNode, textBeforeOffset.length);
    range.setEnd(textNode, textBeforeOffset.length + 1);
  }

  // Points are stable under mutations, whereas ranges are not.
  const rangeStartPoint = createPointWithStartOfRange(range);
  const rangeEndPoint = createPointWithEndOfRange(range);

  // Split the end point first, because then the offsets still make sense if the start and end point are in
  // the same text node.
  let returnEndPoint = rangeEndPoint.hoistSplit(endPointHoistOptions, container);
  let returnStartPoint = rangeStartPoint.hoistSplit(startPointHoistOptions, container);

  // Do deeper splitting/hoisting, which we don't want to return to our caller.
  const startPoint = returnStartPoint.hoist(startPointHoistOptions, container);
  let endPoint = returnEndPoint.hoist(endPointHoistOptions, container);

  if (isPaintingFiller) {
    // For the range we return, collapse it after the filler so the cursor displays.
    returnStartPoint = returnEndPoint;

    if (existingInsertionPoint && existingInsertionPoint.parentNode) {
      // If there's an existing insertion point wrapper already present, hoist up any pre-existing children.
      let childNode = existingInsertionPoint.firstChild;
      while (childNode && startPoint.isAfterNode(childNode)) {
        existingInsertionPoint.parentNode.insertBefore(childNode, existingInsertionPoint);
        childNode = existingInsertionPoint.firstChild;
      }
    } else {
      // Add an insertion point wrapper to keep track of our filler.
      const insertionPoint = createInsertionPoint(document);
      startPoint.insertNode(insertionPoint);

      // After splitting and hoisting existing styles, move them into our insertion point wrapper.
      let childNode = insertionPoint.nextSibling;
      while (childNode && endPoint.isAfterNode(childNode)) {
        insertionPoint.appendChild(childNode);
        childNode = childNode.nextSibling;
      }
    }
  }

  // Sanity check.
  if (
    !startPoint.intersectsNode(container) ||
    !endPoint.intersectsNode(container) ||
    !endPoint.isAfterPoint(startPoint)
  ) {
    throw new PaintPointsInconsistentError(container, startPoint, endPoint);
  }

  let traversalContainer: Node = container;
  let node: Node | null = startPoint.findNextNode(traversalContainer);
  let previousNode: Node | null = null;
  const paintedNodes = new Set<Node>();

  while (node && node !== previousNode) {
    // Failsafe to make sure we don't keep looping over the same node.
    previousNode = node;

    // Narrow our search scope if the current node otherwise contains the end point.
    if (endPoint.intersectsNode(node)) {
      traversalContainer = node;

      // If the current node non-inclusively contains the end point, not all children are in range, and so we
      // traverse down (preorder depth-first).
      if (!endPoint.isAtEndOfNode(node)) {
        node = node.firstChild;
        continue;
      }
    }

    // Stop when we're reached the end.
    if (!endPoint.isAfterNode(node)) {
      node = null;
      break;
    }

    // If this is not phrasing content that needs painting, traverse down (preorder depth-first).
    if (!shouldPaintNode(node)) {
      node = findNextChildOrSibling(node, traversalContainer);
      continue;
    }

    if (createPaintedElement && node.parentNode && !paintedNodes.has(node.parentNode)) {
      const { parentNode } = node;
      let nextSibling: Node | null = node;

      // Collect the sequence of phrasing content that needs painting. While using an intermediate array uses
      // more memory, modifying the DOM in this loop would result in ugly code and also confuses Safari and
      // Edge's Node#compareDocumentPosition().
      const childrenToPaint = [];
      while (nextSibling && endPoint.isAfterNode(nextSibling) && shouldPaintNode(nextSibling)) {
        childrenToPaint.push(nextSibling);
        nextSibling = nextSibling.nextSibling;
      }

      const paintedNode = createPaintedElement(document);
      childrenToPaint.forEach(childNode => {
        paintedNode.appendChild(childNode);
      });

      parentNode.insertBefore(paintedNode, nextSibling);

      previousNode = paintedNode;
      node = paintedNode;
      paintedNodes.add(paintedNode);
    }

    if (isElement(node) && shouldPaintNode(node) && node.parentNode) {
      const oldNode = node;
      const { parentNode, nextSibling } = node;

      const newNode = paintElement(oldNode);

      // The element may have been mutated, but it is not being replaced/removed.
      if (newNode === oldNode) {
        node = findNextChildOrSibling(node, traversalContainer);
        continue;
      }

      const isFragment = !!newNode && newNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE;

      // The element is being removed (replaced with nothing).
      if (!newNode || (isFragment && !newNode.hasChildNodes())) {
        // Figure out where to go next before removing the element. If the end point was at this node, then
        // traversalContainer would've been set to that node and the following will result in null.
        node = findNextSiblingOrAuncle(node, traversalContainer);

        parentNode.removeChild(oldNode);
        continue;
      }

      // The element is being replaced, so readjust our end point and search scope accordingly.
      if (endPoint.isAtEndOfNode(node)) {
        const lastChild = isFragment ? newNode.lastChild! : newNode;
        endPoint = createPointAtEndOfNode(lastChild);

        traversalContainer = isFragment ? parentNode : newNode;
      }

      // Adjust our return points as well.
      if (returnEndPoint.isAtEndOfNode(oldNode)) {
        returnEndPoint = createPointAtEndOfNode(isFragment ? newNode.lastChild! : newNode);
      }
      if (returnStartPoint.isAtStartOfNode(oldNode)) {
        returnStartPoint = createPointAtStartOfNode(isFragment ? newNode.firstChild! : newNode);
      }

      node = isFragment ? newNode.firstChild! : newNode;

      parentNode.removeChild(oldNode);
      parentNode.insertBefore(newNode, nextSibling);
      paintedNodes.add(newNode);
      continue;
    }

    node = findNextSiblingOrAuncle(node, traversalContainer);
  }

  if (node && node === previousNode) {
    if (isKarmaTestEnv) {
      // Hard fail when running tests.
      throw new Error(BAIL_MESSAGE);
    } else {
      // eslint-disable-next-line no-console
      console.error(BAIL_MESSAGE);
    }
  }

  return createRangeWithPoints(document, returnStartPoint, returnEndPoint);
}

class PaintPointsInconsistentError extends Error {
  constructor(container: Node, startPoint: Point, endPoint: Point) {
    let message;

    if (!startPoint.intersectsNode(container)) {
      message = `Start point does not intersect container: ${startPoint}`;
    } else if (!endPoint.intersectsNode(container)) {
      message = `End point does not intersect container: ${endPoint}`;
    } else if (!endPoint.isAfterPoint(startPoint)) {
      message = `End point is not after start point: ${startPoint}, ${endPoint}`;
    } else {
      message = 'Points are inconsistent for an unknown reason';
    }

    super(message);
  }
}
